import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonDropdown, Icon, Typography } from "@/components/atoms";
import { BulkBookingClient } from "@/components/organisms/BulkBooking/BulkBookingContext";
import { BulkBookingModal } from "@/components/organisms/BulkBooking/BulkBookingModal";
import { BulkUploadMenuSection } from "@/components/organisms/BulkBooking/BulkUploadMenuSection";
import { ClientPickerModal, ClientPickerProfile } from "@/components/organisms/ClientPickerModal/ClientPickerModal";
import { useOperator } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";

interface BookTripButtonProps {
  /** "split" = desktop header CTA; "drawer" = full-width entry in the mobile Options drawer. */
  variant?: "split" | "drawer";
  /** Called once a client is picked (before navigation) — lets the mobile drawer close itself. */
  onSelect?: () => void;
}

/**
 * Operator entry into the OBO booking flow (Figma 9208-62110). A split button again as of DEV-1215:
 * the left half opens the client picker, the caret adds the bulk-upload options. Styled from the same
 * caret theme as the Client Manager Actions button, so the two look identical. Picking a client resets
 * any in-flight draft and routes to the operator-scoped booking sheet.
 *
 * The mobile drawer stays a plain button — it's a full-width row in a list of actions, and the drawer
 * lists its options separately.
 */
export const BookTripButton = ({ variant = "split", onSelect }: BookTripButtonProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: operatorId } = useOperator();
  // Which flow the picker is feeding — both need a client first, but one navigates and one opens
  // the bulk wizard.
  const [picking, setPicking] = useState<"book" | "bulk">();
  const [bulkClient, setBulkClient] = useState<BulkBookingClient>();

  const handleSelect = (clientId: string, profile?: ClientPickerProfile) => {
    const purpose = picking;
    setPicking(undefined);

    if (purpose === "bulk") {
      // No accountId means we can't scope the import — leave the picker's work undone rather than
      // opening a wizard that would POST unscoped.
      if (!profile?.accountId) return;
      setBulkClient({ accountId: profile.accountId, name: profile.name, avatarSrc: profile.avatarSrc });
      return;
    }

    dispatch(clearBooking());
    onSelect?.();
    navigate(`/operator/${operatorId}/clients/${clientId}/booking`);
  };

  return (
    <>
      {variant === "split" ? (
        <ButtonDropdown startIcon="Add" menuClassName="w-[298px]" onClick={() => setPicking("book")}>
          <>Book a Trip</>
          <>
            <ButtonDropdown.Item onClick={() => setPicking("book")} className="w-[298px]">
              <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
              <Typography className="text-neutral-black">Book a Trip</Typography>
            </ButtonDropdown.Item>
            <BulkUploadMenuSection onUpload={() => setPicking("bulk")} />
          </>
        </ButtonDropdown>
      ) : (
        <Button variant="tertiary" startIcon="Add" onClick={() => setPicking("book")} className="max-md:w-full max-md:justify-start">
          Book a Trip
        </Button>
      )}

      <ClientPickerModal
        open={!!picking}
        onClose={() => setPicking(undefined)}
        onSelect={handleSelect}
        title={picking === "bulk" ? "Select a Profile to Upload a Bulk Booking" : undefined}
        description={picking === "bulk" ? "Choose a client to upload bulk booking" : undefined}
      />

      <BulkBookingModal open={!!bulkClient} onClose={() => setBulkClient(undefined)} client={bulkClient} />
    </>
  );
};
